<!-- ── Trigger button ─────────────────────────────────────────── -->
<div class="collab-trigger" (click)="togglePanel()">
  <span class="trigger-icon">
    <span *ngIf="connectionStatus === 'connected'" class="status-dot connected"></span>
    <span *ngIf="connectionStatus === 'connecting'" class="status-dot connecting"></span>
    <span *ngIf="connectionStatus === 'disconnected' && !isInRoom" class="status-dot"></span>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  </span>
  <span class="trigger-text">Collab</span>
  <span class="trigger-users" *ngIf="isInRoom">· {{ totalUsers }}</span>
</div>

<!-- ── Profile modal ─────────────────────────────────────────── -->
<app-user-profile-modal
  *ngIf="modalMode"
  [mode]="modalMode!"
  [prefillRoomId]="joinRoomId"
  (confirmed)="onProfileConfirmed($event)"
  (cancelled)="onProfileCancelled()">
</app-user-profile-modal>

<!-- ── Panel ─────────────────────────────────────────────────── -->
<!-- Hide panel while any modal is open so the modal is never obscured -->
<ng-container *ngIf="panelView !== 'closed' && !modalMode">
  <div class="collab-panel open">

    <!-- Copy toast -->
    <div class="copy-toast" *ngIf="copyToast">{{ copyToast }}</div>

    <!-- Header -->
    <div class="panel-header">
      <span class="panel-title">
        <span *ngIf="isInRoom">{{ roomId }}</span>
        <span *ngIf="!isInRoom && !isPending">Collaboration</span>
        <span *ngIf="isPending">Waiting for approval…</span>
      </span>
      <button class="panel-close" (click)="togglePanel()">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <!-- HTTP signaling warning (lobby or pending) -->
    <div class="signal-warn" *ngIf="collabSvc.signalingMode === 'local' && !isInRoom">
      <div class="signal-warn-title">Local Signaling Required</div>
      <div class="signal-warn-body">You're on <strong>HTTP</strong>. Run this first:</div>
      <div class="signal-cmd" (click)="copySignalingCmd()">
        <code>npx y-webrtc-signaling --port 4444</code>
        <span class="copy-hint">copy</span>
      </div>
    </div>

    <!-- ── LOBBY ─────────────────────────────────────────────── -->
    <ng-container *ngIf="!isInRoom && !isPending">
      <div class="user-strip" *ngIf="localUser">
        <span class="avatar-pill" [style.background]="localUser.color">{{ localUser.avatar }}</span>
        <span class="user-name">{{ localUser.username }}</span>
        <button class="btn-ghost xs" (click)="openProfile()">Edit</button>
      </div>
      <div class="user-strip setup" *ngIf="!localUser">
        <button class="btn-primary w100" (click)="openProfile()">Set Up Profile</button>
      </div>

      <div class="lobby-actions">
        <div class="lobby-field">
          <label>Password (optional)</label>
          <input class="pxl-input" type="password" placeholder="Leave blank for open room"
            [(ngModel)]="createPassword" />
        </div>
        <button class="btn-primary" [disabled]="!localUser" (click)="openCreateRoom()">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create Room
        </button>
        <div class="lobby-divider">or</div>
        <div class="lobby-field">
          <label>Room ID</label>
          <input class="pxl-input" type="text" placeholder="ABC1234" [(ngModel)]="joinRoomId" />
        </div>
        <div class="lobby-field">
          <label>Password (if required)</label>
          <input class="pxl-input" type="password" placeholder="Leave blank if none" [(ngModel)]="joinPassword" />
        </div>
        <button class="btn-secondary" [disabled]="!localUser || !joinRoomId" (click)="openJoinRoom()">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M10 17l5-5-5-5"></path>
            <path d="M15 12H3"></path>
            <path d="M21 5v14"></path>
          </svg>
          Join Room
        </button>
      </div>
    </ng-container>

    <!-- ── PENDING ───────────────────────────────────────────── -->
    <ng-container *ngIf="isPending">
      <div class="pending-body">
        <div class="pending-spinner"></div>
        <p>{{ pendingStatusMessage }}</p>
        <p class="pending-hint" *ngIf="pendingStatusHint">{{ pendingStatusHint }}</p>
        <button class="btn-ghost" (click)="leaveRoom()">Cancel</button>
      </div>
    </ng-container>

    <!-- ── IN ROOM ───────────────────────────────────────────── -->
    <ng-container *ngIf="isInRoom">

      <!-- Room info row -->
      <div class="room-info-row">
        <div class="room-id-chip" (click)="copyRoomId()" title="Click to copy Room ID">
          <span class="room-id-text">{{ roomId }}</span>
          <span class="copy-hint">copy</span>
        </div>
        <div class="conn-status" [style.color]="statusColor">{{ statusLabel }}</div>
        <button class="btn-ghost xs leave-btn" (click)="onLeaveClicked()">Leave</button>
      </div>

      <!-- Perf warning -->
      <div class="perf-warn" *ngIf="performanceWarning">{{ performanceWarning }}</div>

      <!-- Tab bar -->
      <div class="tab-bar">
        <button class="tab" [class.active]="roomTab === 'peers'" (click)="roomTab = 'peers'">
          Peers <span class="badge">{{ totalUsers }}</span>
        </button>
        <button class="tab" [class.active]="roomTab === 'requests'" (click)="roomTab = 'requests'"
          *ngIf="isAdmin">
          Requests
          <span class="badge warn" *ngIf="requestsBadge > 0">{{ requestsBadge }}</span>
        </button>
        <button class="tab" [class.active]="roomTab === 'settings'" (click)="roomTab = 'settings'"
          *ngIf="isAdmin">
          Settings
        </button>
      </div>

      <!-- ── PEERS TAB ────────────────────────────────────── -->
      <div class="tab-body" *ngIf="roomTab === 'peers'">

        <!-- Self -->
        <div class="peer-row self" *ngIf="localUser">
          <span class="avatar-pill" [style.background]="localUser.color">{{ localUser.avatar }}</span>
          <div class="peer-info">
            <span class="peer-name">{{ localUser.username }} <span class="you-tag">you</span></span>
            <span class="peer-role">{{ role }}</span>
          </div>
        </div>

        <!-- Remote peers -->
        <div class="peer-row" *ngFor="let peer of peers">
          <span class="avatar-pill" [style.background]="peer.color">{{ peer.avatar }}</span>
          <div class="peer-info">
            <span class="peer-name">{{ peer.username }}</span>
            <span class="peer-meta">
              {{ peer.role }}
              · {{ toolLabel(peer.activeTool) }}
              <ng-container *ngIf="settings.showCursorLabels && peer.cursorX >= 0">
                · ({{ peer.cursorX }}, {{ peer.cursorY }})
              </ng-container>
            </span>
          </div>
          <!-- Admin actions -->
          <div class="peer-actions" *ngIf="isAdmin">
            <button class="btn-ghost xs" title="Promote to admin"
              *ngIf="peer.role !== 'admin'" (click)="promoteUser(peer.username)">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M2 7l5 4 5-8 5 8 5-4-2 13H4z"></path>
              </svg>
            </button>
            <button class="btn-ghost xs" *ngIf="peer.role !== 'admin'"
              [title]="peer.role === 'spectator' ? 'Grant write access' : 'Set read-only access'"
              (click)="togglePeerReadonly(peer)">
              <svg *ngIf="peer.role !== 'spectator'" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 20h9"></path>
                <path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z"></path>
                <line x1="4" y1="4" x2="20" y2="20"></line>
              </svg>
              <svg *ngIf="peer.role === 'spectator'" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 20h9"></path>
                <path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z"></path>
              </svg>
            </button>
            <button class="btn-ghost xs" *ngIf="peer.role !== 'admin'"
              [title]="isPeerChatMuted(peer.username) ? 'Enable chat for this user' : 'Disable chat for this user'"
              (click)="togglePeerChatMute(peer.username)">
              <svg *ngIf="!isPeerChatMuted(peer.username)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <svg *ngIf="isPeerChatMuted(peer.username)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </button>
            <!-- Room creator cannot be demoted -->
            <button class="btn-ghost xs" title="Demote to member"
              *ngIf="peer.role === 'admin' && peer.username !== roomCreator" (click)="demoteUser(peer.username)">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 5v14"></path>
                <path d="m19 12-7 7-7-7"></path>
              </svg>
            </button>
            <span class="creator-badge" *ngIf="peer.role === 'admin' && peer.username === roomCreator" title="Room creator - cannot be demoted">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1Z"></path>
              </svg>
            </span>
            <button class="btn-ghost xs danger" title="Kick &amp; ban" (click)="kickUser(peer.username)">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <div class="empty-peers" *ngIf="peers.length === 0">
          No other peers connected yet.
        </div>

        <!-- Invite -->
        <div class="invite-row">
          <button class="btn-ghost xs w100" (click)="copyRoomLink()">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            Copy invite link
          </button>
        </div>
      </div>

      <!-- ── REQUESTS TAB ─────────────────────────────────── -->
      <div class="tab-body" *ngIf="roomTab === 'requests' && isAdmin">
        <div class="empty-peers" *ngIf="joinRequests.length === 0">No pending requests.</div>
        <div class="request-row" *ngFor="let req of joinRequests">
          <span class="avatar-pill" [style.background]="req.color">{{ req.avatar }}</span>
          <div class="req-content">
            <div class="req-info">
              <span class="peer-name">{{ req.username }}</span>
              <span class="peer-meta">{{ req.email }}</span>
            </div>
            <div class="req-actions">
              <button class="btn-primary xs" (click)="approveRequest(req, 'member')">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Member
              </button>
              <button class="btn-secondary xs" (click)="approveRequest(req, 'spectator')">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z"></path>
                  <line x1="4" y1="4" x2="20" y2="20"></line>
                </svg>
                View Only
              </button>
              <button class="btn-ghost xs danger req-reject" (click)="rejectRequest(req)">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── SETTINGS TAB ─────────────────────────────────── -->
      <div class="tab-body settings-tab" *ngIf="roomTab === 'settings' && isAdmin">
        <div class="settings-section">
          <div class="settings-section-title">Frame Permissions</div>
          <label class="toggle-row">
            <span>Admin-only frame creation</span>
            <input type="checkbox" [(ngModel)]="settingsEdit.adminOnlyFrameCreate" (change)="onSettingChange()" />
          </label>
          <label class="toggle-row">
            <span>Admin-only frame deletion</span>
            <input type="checkbox" [(ngModel)]="settingsEdit.adminOnlyFrameDelete" (change)="onSettingChange()" />
          </label>
        </div>

        <div class="settings-section">
          <div class="settings-section-title">Member Permissions</div>
          <label class="toggle-row">
            <span>Draw-only mode <span class="hint">(no layers, timeline, or non-canvas tools)</span></span>
            <input type="checkbox" [(ngModel)]="settingsEdit.membersDrawOnly" (change)="onSettingChange()" />
          </label>
          <label class="toggle-row">
            <span>Allow members to save canvas snapshots</span>
            <input type="checkbox" [(ngModel)]="settingsEdit.allowMembersSaveCanvas" (change)="onSettingChange()" />
          </label>
        </div>

        <div class="settings-section">
          <div class="settings-section-title">Sync &amp; Display</div>
          <label class="toggle-row">
            <span>Broadcast generations</span>
            <input type="checkbox" [(ngModel)]="settingsEdit.broadcastAiGenerations" (change)="onSettingChange()" />
          </label>
          <label class="toggle-row">
            <span>Show cursor labels on canvas</span>
            <input type="checkbox" [(ngModel)]="settingsEdit.showCursorLabels" (change)="onSettingChange()" />
          </label>
          <label class="toggle-row">
            <span>Enable chat for members</span>
            <input type="checkbox" [(ngModel)]="settingsEdit.chatEnabled" (change)="onSettingChange()" />
          </label>
        </div>

        <div class="settings-section">
          <div class="settings-section-title">Room Security</div>
          <div class="settings-field">
            <label>Room Password</label>
            <input class="pxl-input" type="password" placeholder="Leave blank for open room"
              [(ngModel)]="settingsEdit.roomPassword" (input)="onSettingChange()" />
            <span class="hint">New joiners will need this password.</span>
          </div>
          <div class="settings-field">
            <label>Max Users</label>
            <input class="pxl-input" type="number" min="2" max="50"
              [(ngModel)]="settingsEdit.maxUsers" (input)="onSettingChange()" />
            <span class="hint">Maximum users allowed in this room (2–50).</span>
          </div>
          <label class="toggle-row">
            <span>Allow members to auto-rejoin on refresh</span>
            <input type="checkbox" [(ngModel)]="settingsEdit.autoRejoin" (change)="onSettingChange()" />
          </label>
        </div>

        <div class="settings-section" *ngIf="bannedUsers.length > 0">
          <div class="settings-section-title">Banned Users</div>
          <div class="banned-row" *ngFor="let username of bannedUsers">
            <span class="banned-name">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"></circle>
                <line x1="5" y1="5" x2="19" y2="19"></line>
              </svg>
              {{ username }}
            </span>
            <button class="btn-ghost xs" (click)="unbanUser(username)" title="Unban - allows them to request to join again">Unban</button>
          </div>
        </div>

        <div class="settings-actions">
          <button class="btn-primary" [disabled]="!settingsDirty" (click)="saveSettings()">Save Changes</button>
          <button class="btn-ghost" [disabled]="!settingsDirty" (click)="resetSettings()">Reset</button>
        </div>
      </div>

    </ng-container>
  </div>
</ng-container>

<div class="leave-admin-backdrop" *ngIf="showAdminLeaveModal" (click)="cancelAdminLeaveModal()">
  <div class="leave-admin-modal" (click)="$event.stopPropagation()">
    <div class="leave-admin-title">Leave Collaboration</div>
    <div class="leave-admin-copy">Choose what should happen before you leave.</div>
    <div class="leave-admin-actions">
      <button *ngIf="isRoomCreator" class="pxl-btn pxl-btn-danger w100" (click)="closeRoomAndLeave()">Close Room</button>
      <div *ngIf="isRoomCreator" class="leave-admin-sep">or</div>
      <label class="leave-admin-label">Transfer Admin &amp; Leave</label>
      <select class="pxl-input" [(ngModel)]="leaveTransferTarget">
        <option value="" disabled>Select a member</option>
        <option *ngFor="let peer of transferCandidates" [value]="peer.username">{{ peer.username }}</option>
      </select>
      <button class="btn-primary w100" [disabled]="!leaveTransferTarget" (click)="transferAdminAndLeave()">Transfer Admin &amp; Leave</button>
      <button *ngIf="!isRoomCreator" class="btn-secondary w100" (click)="leaveRoom()">Leave (keep room open)</button>
      <div *ngIf="!isRoomCreator" class="leave-admin-note">Only the original creator can close the room for everyone.</div>
      <div class="leave-admin-note">Original room creator cannot be demoted.</div>
    </div>
    <div class="leave-admin-footer">
      <button class="btn-ghost" (click)="cancelAdminLeaveModal()">Cancel</button>
    </div>
  </div>
</div>
